import { localizedBoardLabel } from '@voll-intelligence/genui-contract';
import type { CheckoutCopy, ConciergeCopy } from '@voll-intelligence/intelligence-ui-kit/checkout';
import { flightPolicyReasons } from '../../../../shared/ui/flight_policy_reasons.js';
import { genUiI18n, type GenUiTranslate } from '../../../../shared/ui/i18n.js';
import { checkoutFieldCopy } from './field_copy.js';

/*
 * The checkout organism's texts (#5000) from the genui catalog, in the host's
 * language: the conversation and the deterministic pages show the same words.
 */

/** Picks the `_one`/`_other` message by the count. */
function plural(
  t: GenUiTranslate,
  one: Parameters<GenUiTranslate>[0],
  other: Parameters<GenUiTranslate>[0],
  count: number,
  values: Record<string, string | number> = {},
): string {
  return t(count === 1 ? one : other, { count, ...values });
}

function conciergeCopy(t: GenUiTranslate): ConciergeCopy {
  const period = {
    morning: t('conciergeCheckout.period.morning'),
    afternoon: t('conciergeCheckout.period.afternoon'),
    night: t('conciergeCheckout.period.night'),
    dawn: t('conciergeCheckout.period.dawn'),
  };
  return {
    title: t('conciergeCheckout.title'),
    directChip: t('flightFilter.stops.direct'),
    chip: {
      flight: t('conciergeCheckout.chip.flight'),
      hotel: t('conciergeCheckout.chip.hotel'),
      bus: t('conciergeCheckout.chip.bus'),
      rental: t('conciergeCheckout.chip.rental'),
    },
    card: {
      flight: {
        title: t('conciergeCheckout.flight.cardTitle'),
        detailsPlaceholder: t('conciergeCheckout.flight.detailsPlaceholder'),
      },
      hotel: {
        title: t('conciergeCheckout.hotel.cardTitle'),
        detailsPlaceholder: t('conciergeCheckout.hotel.detailsPlaceholder'),
        detailsHint: t('conciergeCheckout.hotel.detailsHint'),
      },
      bus: {
        title: t('conciergeCheckout.bus.cardTitle'),
        detailsPlaceholder: t('conciergeCheckout.bus.detailsPlaceholder'),
        detailsHint: t('conciergeCheckout.bus.detailsHint'),
      },
      rental: {
        title: t('conciergeCheckout.rental.cardTitle'),
        detailsPlaceholder: t('conciergeCheckout.rental.detailsPlaceholder'),
        detailsHint: t('conciergeCheckout.rental.detailsHint'),
      },
    },
    noPriceBadge: t('conciergeCheckout.noPriceBadge'),
    profileLabel: t('conciergeCheckout.profileLabel'),
    profileHint: t('conciergeCheckout.profileHint'),
    optional: t('conciergeCheckout.optional'),
    detailsLabel: t('conciergeCheckout.detailsLabel'),
    reasonTitle: t('conciergeCheckout.reasonTitle'),
    reasonLoading: t('conciergeCheckout.reasonLoading'),
    period,
    flight: {
      asksTitle: t('conciergeCheckout.flight.asksTitle'),
      direct: t('conciergeCheckout.flight.direct'),
      luggage: t('conciergeCheckout.flight.luggage'),
      periodAny: t('conciergeCheckout.flight.periodAny'),
      periodFor: (route, date) => t('conciergeCheckout.flight.periodFor', { route, date }),
    },
    hotel: {
      preferredHotel: t('conciergeCheckout.hotel.preferredHotel'),
      preferredHotelPlaceholder: t('conciergeCheckout.hotel.preferredHotelPlaceholder'),
      bedType: t('conciergeCheckout.hotel.bedType'),
      bed: {
        UNDEFINED: t('conciergeCheckout.hotel.bed.UNDEFINED'),
        SINGLE: t('conciergeCheckout.hotel.bed.SINGLE'),
        DOUBLE: t('conciergeCheckout.hotel.bed.DOUBLE'),
      },
    },
    rental: {
      category: t('conciergeCheckout.rental.category'),
      company: t('conciergeCheckout.rental.company'),
      companyAny: t('conciergeCheckout.rental.companyAny'),
      loading: t('conciergeCheckout.rental.optionsLoading'),
      failed: t('conciergeCheckout.rental.optionsFailed'),
      retry: t('conciergeCheckout.rental.retry'),
    },
    bus: {
      asksTitle: t('conciergeCheckout.bus.asksTitle'),
      choose: t('conciergeCheckout.bus.choose'),
      leg: {
        outbound: t('conciergeCheckout.bus.leg.outbound'),
        return: t('conciergeCheckout.bus.leg.return'),
      },
      periodFor: (leg, date) => t('conciergeCheckout.bus.periodFor', { leg, date }),
      seatPosition: t('conciergeCheckout.bus.seatPosition'),
      seatPositions: {
        FRONT: t('conciergeCheckout.bus.seatPosition.FRONT'),
        MIDDLE: t('conciergeCheckout.bus.seatPosition.MIDDLE'),
        BACK: t('conciergeCheckout.bus.seatPosition.BACK'),
      },
      rowPosition: t('conciergeCheckout.bus.rowPosition'),
      rowPositions: {
        WINDOW_SEAT: t('conciergeCheckout.bus.rowPosition.WINDOW_SEAT'),
        AISLE_SEAT: t('conciergeCheckout.bus.rowPosition.AISLE_SEAT'),
      },
      seatClass: t('conciergeCheckout.bus.seatClass'),
      seatClasses: {
        ALL: t('conciergeCheckout.bus.seatClass.ALL'),
        CONVENTIONAL: t('conciergeCheckout.bus.seatClass.CONVENTIONAL'),
        EXECUTIVE: t('conciergeCheckout.bus.seatClass.EXECUTIVE'),
        HALF_BED: t('conciergeCheckout.bus.seatClass.HALF_BED'),
        BED: t('conciergeCheckout.bus.seatClass.BED'),
        BED_FULL: t('conciergeCheckout.bus.seatClass.BED_FULL'),
      },
    },
    panel: {
      flightItem: (route) => t('conciergeCheckout.panel.flightItem', { route }),
      hotelItem: t('conciergeCheckout.panel.hotelItem'),
      busItem: (route) => t('conciergeCheckout.panel.busItem', { route }),
      rentalItem: t('conciergeCheckout.panel.rentalItem'),
      toQuote: t('conciergeCheckout.panel.toQuote'),
      requested: t('conciergeCheckout.panel.requested'),
      heldOutbound: t('conciergeCheckout.panel.heldOutbound'),
      totalToQuote: t('conciergeCheckout.panel.totalToQuote'),
      totalWithHeld: (held) => t('conciergeCheckout.panel.totalWithHeld', { held }),
      totalLabel: t('conciergeCheckout.panel.totalLabel'),
      inProgress: t('conciergeCheckout.panel.inProgress'),
      note: {
        flight: t('conciergeCheckout.panel.flightNote'),
        hotel: t('conciergeCheckout.panel.hotelNote'),
        bus: t('conciergeCheckout.panel.busNote'),
        rental: t('conciergeCheckout.panel.rentalNote'),
      },
    },
    submit: t('conciergeCheckout.submit'),
    back: t('conciergeCheckout.back'),
  };
}

/** What still blocks the submit, named next to the CTA. */
function blockersCopy(t: GenUiTranslate): CheckoutCopy['blockers'] {
  return {
    list: (items) => t('productCheckout.blockers', { items }),
    fields: {
      birthdate: t('productCheckout.blocker.birthdate'),
      nationality: t('productCheckout.blocker.nationality'),
      gender: t('productCheckout.blocker.gender'),
      document: t('productCheckout.blocker.document'),
      embark: t('productCheckout.blocker.embark'),
      phone: t('productCheckout.blocker.phone'),
      allocation: t('productCheckout.blocker.allocation'),
      project: t('productCheckout.blocker.project'),
      justification: t('productCheckout.blocker.justification'),
    },
  };
}

/** Every text of the checkout organism in the host's language. */
export function checkoutCopy(locale: string | null | undefined): CheckoutCopy {
  const { t, locale: lang } = genUiI18n(locale);
  const product = (type: 'rental' | 'hotel' | 'flight' | 'bus' | 'mobility') => ({
    chip: t(`productCheckout.chipLabel.${type}`),
    noun: t(`productCheckout.noun.${type}`),
  });
  return {
    fields: checkoutFieldCopy(t),
    shell: {
      title: t('productCheckout.drawerTitle'),
      close: t('productCheckout.closeAria'),
      agentLine: t('productCheckout.agentLine'),
      loading: t('productCheckout.loading'),
      loadingAria: t('productCheckout.loadingAria'),
      error: t('productCheckout.errorMessage'),
      retry: t('common.tryAgain'),
      dockTotal: t('productCheckout.price.total'),
      dockSubmit: t('productCheckout.dock.submit'),
    },
    products: {
      rental: product('rental'),
      hotel: product('hotel'),
      flight: product('flight'),
      bus: product('bus'),
      mobility: product('mobility'),
    },
    motive: {
      trip: {
        title: t('productCheckout.motiveTitle'),
        blocker: t('productCheckout.blocker.motive'),
      },
      ride: {
        title: t('productCheckout.mobility.motiveTitle'),
        blocker: t('productCheckout.mobility.blockerMotive'),
      },
    },
    blockers: blockersCopy(t),
    price: {
      total: t('productCheckout.price.total'),
      tripTotal: t('productCheckout.price.tripTotal'),
      perTraveler: t('productCheckout.price.perTraveler'),
      dailies: (count) =>
        plural(
          t,
          'productCheckout.price.dailies_one',
          'productCheckout.price.dailies_other',
          count,
        ),
      eachDaily: (amount) => t('productCheckout.price.eachDaily', { amount }),
      approvalNote: t('productCheckout.price.approvalNote'),
      submitWithTotal: (noun, total) =>
        t('productCheckout.action.submitWithTotal', { product: noun, total }),
      submitting: t('productCheckout.submitting'),
      back: t('productCheckout.back'),
      retry: t('productCheckout.retry'),
    },
    traveler: {
      you: t('travelers.you'),
      edit: t('productCheckout.action.edit'),
      done: t('productCheckout.action.done'),
      genderFemale: t('productCheckout.field.genderFemale'),
      genderMale: t('productCheckout.field.genderMale'),
      phonePending: t('productCheckout.phonePending'),
      inheritedHint: t('productCheckout.inheritedHint'),
      inheritedHintOther: t('productCheckout.inheritedHintOther'),
      justification: t('productCheckout.fieldJustification'),
      justificationOptional: t('productCheckout.fieldJustificationOptional'),
      selectPlaceholder: t('productCheckout.selectPlaceholder'),
      policyLabel: t('travelers.policyLabel'),
      noCorporateAccess: t('productCheckout.hint.noCorporateAccess'),
      outOfPolicy: t('productCheckout.hint.outOfPolicy'),
      allocationFromRequester: t('productCheckout.hint.allocationFromRequester'),
      allocationFromGuest: (name) => t('productCheckout.hint.allocationFromGuest', { name }),
      // O aviso de justificativa obrigatória nomeia a regra e a janela (#5262);
      // sem motivo nomeável, o aviso genérico de sempre.
      outOfPolicyReasons: (violations) => {
        const reasons = flightPolicyReasons(violations, t, lang);
        return reasons.length === 0
          ? undefined
          : t('productCheckout.hint.outOfPolicyReasons', { reasons: reasons.join('; ') });
      },
    },
    trip: {
      sectionTitle: t('productCheckout.trip.sectionTitle'),
      title: (city) => t('productCheckout.trip.title', { city }),
      nights: (count) =>
        plural(t, 'productCheckout.trip.nights_one', 'productCheckout.trip.nights_other', count),
      alreadyBooked: t('productCheckout.trip.alreadyBooked'),
      beingRequested: t('productCheckout.trip.beingRequested'),
      inPolicy: t('booking.inPolicy'),
      travelers: (count) => t('travelers.count_other', { count }),
    },
    budget: {
      insufficient: t('productCheckout.budget.insufficient'),
      available: t('productCheckout.budget.available'),
      warning: t('productCheckout.budget.warning'),
      sufficient: t('productCheckout.budget.sufficient'),
      sufficientIn: (costCenter) => t('productCheckout.budget.sufficientIn', { costCenter }),
    },
    hotel: {
      room: t('hotel.room'),
      stayAria: t('productCheckout.hotelStay'),
      nights: (count) => plural(t, 'hotelRooms.nights_one', 'hotelRooms.nights_other', count),
      nonRefundable: t('productCheckout.nonRefundable'),
      refundable: t('productCheckout.refundable'),
      freeCancelUntil: (date) => t('hotelRooms.freeCancelUntil', { date }),
      board: (board) => localizedBoardLabel(board, lang),
    },
    bus: {
      leg: { outbound: t('busOffers.outbound'), return: t('busOffers.return') },
      direct: t('common.direct'),
      connections: (count) =>
        plural(t, 'busOffers.connection_one', 'busOffers.connection_other', count),
      noSeat: t('busSeatMap.noSeatSelected'),
      seats: t('productCheckout.bus.seats'),
      segment: (n) => t('productCheckout.bus.segment', { n }),
      fareClass: t('booking.fareClass'),
    },
    mobility: {
      sectionRide: t('mobility.sectionRide'),
      sectionCarDriver: t('mobility.sectionCarDriver'),
      stops: (stops) =>
        plural(t, 'mobility.stops_one', 'mobility.stops_other', stops.length, {
          stops: stops.join(' · '),
        }),
      days: (count) => plural(t, 'mobility.day_one', 'mobility.day_other', count),
      hoursPerDay: (hours) => t('mobility.hoursPerDay', { hours }),
      freeKm: t('mobility.freeKm'),
      distanceKm: (km) => t('mobility.distanceKm', { km }),
      excludesStops: t('mobility.excludesStops'),
      bilingual: t('mobility.bilingual'),
      overnight: (count) => plural(t, 'mobility.overnight_one', 'mobility.overnight_other', count),
    },
    rental: {
      dateAria: t('productCheckout.rentalDate'),
      daysAria: t('productCheckout.rentalDays'),
      pickupAria: t('productCheckout.pickupLocation'),
    },
    concierge: conciergeCopy(t),
  };
}
